import { Box, MenuItem, TableBody, TableCell, TableRow, ThemeProvider, Toolbar, createTheme } from "@mui/material";
import React, { useEffect } from "react";
import { NumericFormat } from "react-number-format";
import Moment from "react-moment";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import {
  AdminRfq,
  AdminRfqQueryParameter,
  AdminRfqsState,
  RfqStatusCode,
  adminRfqStatus,
  isAdminRfqsQueryEmpty
} from "../../models/AdminRfqsModels";
import { useAppDispatch, useAppSelector } from "../../../shared/hooks/sharedHooks";
import SortTableHead, { TableColumnDefinition } from "../../../shared/components/SortTableHead";
import ActionToast from "../../../shared/components/ActionToast";
import Loader from "../../../shared/components/Loader";
import BoxSearchIcon from "../../../shared/assets/icons/BoxSearchIcon";
import TableEmpty from "../../../shared/components/tables/TableEmpty";
import Table from "../../../shared/components/tables/Table";
import {
  changeQuery,
  deleteAdminRfq,
  getAdminRfqs,
  getCategories,
  getManufacturers,
  setError,
  setLoading,
  setSuccess
} from "../../adminRfqsSlice";
import TablePagination from "../../../shared/components/tables/TablePagination";
import { NotificationMessage, getName } from "../../../shared/models/models";
import TableActionButton from "../../../shared/components/tables/TableActionButton";
import { useConfirmation } from "../../../shared/components/confirmation/ConfirmationProvider";

const Rfqs = () => {
  const { query, rfqs, loading, success, error } = useAppSelector<AdminRfqsState>((state: any) => state.adminRfqs);

  const dispatch = useAppDispatch();
  const confirm = useConfirmation();

  const columns: TableColumnDefinition[] = [
    // { title: "Country", parameter: AdminRfqQueryParameter.countryName },
    { title: "Company", parameter: AdminRfqQueryParameter.companyName },
    { title: "Product", parameter: AdminRfqQueryParameter.productName },
    { title: "Quantity" },
    { title: "Price", parameter: AdminRfqQueryParameter.price },
    { title: "RFQ details", parameter: AdminRfqQueryParameter.createdDate },
    { title: "Visibility" },
    { title: "status", parameter: AdminRfqQueryParameter.status },
    { title: undefined }
  ];

  const isQueryEmpty = isAdminRfqsQueryEmpty(query);

  const handleSort = (parameter: number) => {
    const sortDesc = query.sortBy !== parameter ? false : !query.sortDesc;
    const newQuery = { ...query, sortBy: parameter, sortDesc: sortDesc };

    dispatch(changeQuery(newQuery));
    dispatch(getAdminRfqs(newQuery));
  };

  const handlePageSizeChange = async (size: number) => {
    const newQuery = { ...query, pageNo: 1, pageSize: size };
    await dispatch(changeQuery(newQuery));
    dispatch(getAdminRfqs(newQuery));
  };

  const handlePageNoChange = async (no: number) => {
    const newQuery = { ...query, pageNo: no };
    await dispatch(changeQuery(newQuery));
    dispatch(getAdminRfqs(newQuery));
  };

  const handleCloseRfq = async (item: AdminRfq) => {
    if (item) {
      await dispatch(deleteAdminRfq(item.id));
      await dispatch(getAdminRfqs(query));
    }
  };

  const init = async () => {
    dispatch(setLoading(true));
    await dispatch(getAdminRfqs(query));
    await dispatch(getManufacturers());
    await dispatch(getCategories());
    dispatch(setLoading(false));
  };

  useEffect(() => {
    init();
  }, []);

  const theme = createTheme({
    components: {
      MuiToolbar: {
        styleOverrides: {
          root: {
            background: "#fff",
            borderBottom: "1px solid #d5d6d9"
          }
        }
      }
    }
  });

  return (
    <ThemeProvider theme={theme}>
      <ActionToast
        open={!!success}
        variant="success"
        message={success || ""}
        onClose={() => dispatch(setSuccess(undefined))}
        duration={6000}
      />

      <ActionToast
        open={!!error}
        variant="error"
        message={NotificationMessage.error(error).message || ""}
        onClose={() => dispatch(setError(undefined))}
        duration={6000}
      />

      <Toolbar className="flex items-center justify-between">
        <Box className="heading-2">RFQs</Box>
      </Toolbar>

      <Box className="px-4 v-fill">
        {loading ? (
          <Loader />
        ) : !rfqs?.items.length ? (
          <TableEmpty
            iconComponent={BoxSearchIcon}
            text={isQueryEmpty ? "You don’t have any items \nin your RFQs" : "You don’t have such items \nin your RFQs"}
          ></TableEmpty>
        ) : (
          <Table>
            <SortTableHead columns={columns} sortBy={query.sortBy} sortDesc={query.sortDesc} onSort={handleSort} />

            <TableBody>
              {rfqs?.items.map((item) => {
                return (
                  <TableRow key={item.id}>
                    {/* <TableCell>country</TableCell> */}

                    <TableCell className="break-all" sx={{ maxWidth: "250px" }}>
                      <Box className="text-14-bold" color={"#333333"}>
                        {item.buyer.name}
                      </Box>
                    </TableCell>

                    <TableCell sx={{ width: "25%" }}>
                      <Box className="block">
                        <Box className="text-12-medium" color={"#333333"} sx={{ wordBreak: "break-all" }}>
                          {item.productNumber}
                        </Box>

                        <Box className="text-12-regular" sx={{ wordBreak: "break-all" }}>
                          {item.productAlternativeNumber}
                        </Box>

                        <Box className="mt-2">
                          <Box className="text-12-medium" color={"#333333"}>
                            {item.manufacturer.name}
                          </Box>
                          <Box className="text-12-regular" color={"#747881"}>
                            {item.category.name}
                          </Box>
                        </Box>
                      </Box>
                    </TableCell>

                    <TableCell>
                      <Box className="text-14-medium" color={"#333333"}>
                        {item.quantity}
                      </Box>
                    </TableCell>

                    <TableCell>
                      {item.maxUnitPrice?.value ? (
                        <Box className="text-14-medium" color={"#333333"}>
                          <NumericFormat
                            value={item.maxUnitPrice?.value}
                            displayType="text"
                            thousandSeparator=" "
                            suffix="€"
                          />
                        </Box>
                      ) : (
                        <Box className="text-14-medium" color={"#333333"}>
                          Negotiable
                        </Box>
                      )}
                    </TableCell>

                    <TableCell sx={{ minWidth: "200px" }}>
                      <Box>
                        <Box className="text-12-regular" color={"#747881"}>
                          Created on{"\u00A0"}
                          <Moment date={item.createdDate} format="DD.MM.YYYY" />
                        </Box>

                        <Box className="text-12-regular" color={"#747881"}>
                          {"#" + item.number}
                        </Box>

                        <Box className="text-12-medium" color={"#333333"}>
                          Last call on{"\u00A0"}
                          <Moment date={item.lastCall} format="DD.MM.YYYY" />
                        </Box>
                      </Box>
                    </TableCell>

                    <TableCell>
                      <Box className="text-14-medium" color={"#333333"}>
                        {item.isPrivate ? "Custom" : "Public"}
                      </Box>
                    </TableCell>

                    <TableCell>
                      <Box className="text-14-medium" color={item.statusCode === RfqStatusCode.open ? "#3EA293" : ""}>
                        {adminRfqStatus(item.statusCode)}
                      </Box>
                    </TableCell>

                    <TableCell>
                      <TableActionButton stopPropagation={true}>
                        <MenuItem
                          onClick={() =>
                            confirm({
                              title: "Delete RFQ",
                              text: `Are you sure you want to delete ${getName(
                                item.manufacturer.name,
                                item.productNumber
                              )} RFQ?`,
                              confirmButton: "Yes, delete",
                              closeButton: "No",
                              severity: "error",
                              onConfirm: () => handleCloseRfq(item)
                            })
                          }
                        >
                          <DeleteOutlinedIcon />
                          Delete
                        </MenuItem>
                      </TableActionButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Box>

      <TablePagination
        variant="bottom"
        pageNo={query.pageNo}
        pageSize={query.pageSize}
        totalCount={rfqs?.totalCount}
        onPageNoChange={handlePageNoChange}
        onPageSizeChange={handlePageSizeChange}
      />
    </ThemeProvider>
  );
};
export default Rfqs;